import type { PromptsSearchBarProps } from "../types";

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Select, SelectItem } from "@heroui/react";

import { SearchIcon } from "@/components/icons";
import { availableLanguages } from "@/i18n";

export const PromptsSearchBar: React.FC<PromptsSearchBarProps> = ({
  searchQuery,
  onSearchChange,
  onCreateNew,
  languageFilter,
  onLanguageChange,
}) => {
  const { t } = useTranslation();

  const languageOptions = useMemo(
    () => [
      { key: "", label: t("All Languages") },
      ...availableLanguages.map((lang) => ({ key: lang.code, label: lang.nativeName })),
    ],
    [t],
  );

  return (
    <div
      className="mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4"
      data-id="prompts-search"
    >
      <Button
        className="btn-brand-primary text-sm"
        data-id="create-prompt-button"
        onPress={onCreateNew}
      >
        {t("Add New Prompt")}
      </Button>
      <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
        <Input
          className="w-full sm:w-60"
          classNames={{
            label: "text-black/50 dark:text-white/90",
            input: [
              "bg-transparent",
              "shadow-none outline-none focus:outline-none focus-within:outline-none",
              "text-black/90 dark:text-default-400",
              "placeholder:text-default-700/50 dark:placeholder:text-default-200/50",
            ],
            innerWrapper: "bg-transparent",
            inputWrapper: [
              "shadow-lg",
              "bg-brand-secondary",
              "dark:bg-default/60",
              "backdrop-blur-lg",
              "backdrop-saturate-200",
              "hover:bg-default-200/70",
              "dark:hover:bg-default/70",
              "group-data-[focus=true]:bg-brand-secondary",
              "dark:group-data-[focus=true]:bg-default/60",
              "!cursor-text",
              "border border-brand-primary hover:border-brand-primary focus-within:border-brand-primary",
            ],
          }}
          data-id="prompts-search-input"
          placeholder={t("Search for a prompt...")}
          size="sm"
          startContent={<SearchIcon className="text-default-400 dark:text-default-300" />}
          value={searchQuery}
          onValueChange={onSearchChange}
        />
        <Select
          className="w-full sm:w-48"
          data-id="prompt-language-filter"
          label={t("Language")}
          placeholder={t("All Languages")}
          selectedKeys={languageFilter ? [languageFilter] : []}
          size="sm"
          onChange={(e) => onLanguageChange(e.target.value)}
        >
          {languageOptions.map((option) => (
            <SelectItem key={option.key}>{option.label}</SelectItem>
          ))}
        </Select>
      </div>
    </div>
  );
};
